import { createContext, useContext } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { type UIConfig } from '../../../types';
import {
	type ChatRefreshV2Config,
	getChatRefreshV2Config,
	initialState as initialV2DynamicConfig,
} from '../chat-refresh-v2-config';

type DynamicConfig = {
	rovoChatRefreshV2Config: ChatRefreshV2Config;
};

type ContextState = UIConfig & { dynamicConfig: DynamicConfig };

const initialState: ContextState = {
	fullScreen: false,
	externalEmbed: false,
	customHandlers: {
		handleDeepLinkClick: undefined,
		handleSubmitSmartCreate: () => {},
	},
	isBitbucketBetaEnabled: false,
	stagingAreaRef: undefined,
	promptInputDroppableUrls: undefined,
	enableBrowserContextBanner: false,
	browserContextSanitizer: undefined,
	elements: {
		closeButton: true,
		newChatButton: true,
		fullscreenButton: true,
		createAgentButton: true,
		browseAgentsButton: true,
		agentsMenu: true,
		navbar: true,
		mainChatHeader: true,
		stagingAreaEnabled: false,
		voiceDictationButton: false,
		fileUploadButton: true,
		chatModeMenuButton: true,
	},
	dynamicConfig: {
		rovoChatRefreshV2Config: initialV2DynamicConfig,
	},
};

export const uiConfigDefaults = initialState;

const uiConfig = createContext<ContextState>(initialState);
export const UIConfigProvider = uiConfig.Provider;
export const useUIConfig = () => useContext(uiConfig);

export const createUIConfig = (config?: Partial<UIConfig>): ContextState => {
	// Allow fullscreen override for branch deploy testing purposes
	const processedConfig: Partial<UIConfig> | undefined =
		localStorage.getItem('DEBUG_rovo_chat_fullscreen') === 'true' &&
		fg('rovo_chat_show_local_frontend_components')
			? {
					...config,
					fullScreen: true,
				}
			: config;

	const rovoChatRefreshV2Config = getChatRefreshV2Config();

	return {
		...{
			...initialState,
			...processedConfig,
			enableBrowserContextBanner:
				processedConfig?.enableBrowserContextBanner ?? initialState.enableBrowserContextBanner,
			browserContextSanitizer: processedConfig?.browserContextSanitizer,
			elements: {
				...initialState.elements,
				...processedConfig?.elements,
			},
			promptInputDroppableUrls: processedConfig?.promptInputDroppableUrls,
		},
		dynamicConfig: {
			...initialState.dynamicConfig,
			rovoChatRefreshV2Config: {
				...rovoChatRefreshV2Config,
			},
		},
	};
};
